import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { ThreadMessage } from './types';
import SentEmailPeek from './SentEmailPeek';
import AttachmentChips from './AttachmentChips';
import { CompactError, Spinner } from '../../ui';

/**
 * One thread message, mock Step 4 styling: buyer bubbles right in action-blue
 * wash, supplier bubbles left in a neutral wash, attribution line underneath.
 *
 * An outbound bubble is the buyer's QUERY — their words, which is what they
 * actually wrote. The email Robbie composed from it sits read-only behind the
 * peek. Messages sent before queries were recorded have no query to show, so
 * their bubble stays the email it always was.
 */
interface MessageBubbleProps {
  message: ThreadMessage;
  /** Who the pending/failed line names — the thread's supplier. */
  supplierName?: string;
  onRetry?: (message: ThreadMessage) => void;
}

/** The bubble's footer while its send is pending or after it failed. */
const SendStateLine: React.FC<{ message: ThreadMessage; supplierName?: string; onRetry?: (m: ThreadMessage) => void }> = ({
  message,
  supplierName,
  onRetry,
}) =>
  message.sendState === 'sending' ? (
    <span role="status" className="inline-flex items-center gap-s1 text-[10px] font-semibold text-ink-muted">
      <Spinner size="sm" /> Sending to {supplierName || 'the supplier'}…
    </span>
  ) : (
    <CompactError
      layout="tiny"
      title="Not sent"
      message={message.sendFailure || 'Nothing left the platform.'}
      action={onRetry ? { label: 'Try again', onClick: () => onRetry(message) } : undefined}
    />
  );

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, supplierName, onRetry }) => {
  const isBuyer = message.sender === 'buyer';

  if (isBuyer) {
    // Nothing to peek at while the send is still in flight: the email does not
    // exist yet, and the bubble is already the query.
    const sentEmail = message.text && message.text !== message.sourceQuery ? message.text : undefined;
    const failed = message.sendState === 'failed';
    return (
      <div
        className={`max-w-[78%] self-end rounded-[10px] rounded-br-[3px] border px-[12px] py-[10px] ${
          failed ? 'border-danger-border bg-surface' : 'border-action-border bg-action-bg'
        }`}
      >
        <div className="whitespace-pre-wrap text-body-sm leading-[1.55] text-ink-body">
          {message.sourceQuery || message.text}
        </div>
        {sentEmail && <SentEmailPeek email={sentEmail} />}
        {message.sendState ? (
          <div className="mt-s2 flex justify-end">
            <SendStateLine message={message} supplierName={supplierName} onRetry={onRetry} />
          </div>
        ) : (
        <div className="mt-s2 flex items-center justify-end gap-[5px]">
          <span className="text-[10px] font-semibold text-ink-muted">
            {message.senderName} · {message.timestamp}
          </span>
          {message.status === 'FAILED' && (
            <span className="inline-flex items-center gap-[3px] text-[10px] font-bold text-danger-ink" title={message.errorDetails}>
              <AlertTriangle className="h-[10px] w-[10px]" /> Failed to send
            </span>
          )}
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-[78%] self-start rounded-[10px] rounded-bl-[3px] border border-line bg-canvas px-[12px] py-[10px]">
      <div className="whitespace-pre-wrap text-body-sm leading-[1.55] text-ink-body">{message.text}</div>
      {message.attachments && message.attachments.length > 0 && (
        <AttachmentChips attachments={message.attachments} />
      )}
      <div className="mt-s2 text-[10px] text-ink-muted">
        {message.senderName} · {message.timestamp}
      </div>
    </div>
  );
};

export default MessageBubble;
